import React from "react";
import type { AppPosition } from "@src/types";
import { formatDateTimeUTC } from "@src/utils/dateUtils";
import { ENTRY_ACTION_IDS, EXIT_ACTION_IDS } from "@src/utils/constants";
import {
  EXECUTION_INTERVAL_UNAVAILABLE,
  type ExecutionInterval,
  NO_RECORDED_MOVEMENTS,
  OUTSIDE_EXECUTION_INTERVAL_LABEL,
  getTrackingEndedMovementTime,
  isFactOutsideExecutionInterval,
} from "@src/utils/positionStatus";
import { TrackingEndedBadge } from "./TrackingEndedBadge";

interface Props {
  position: AppPosition;
  executionInterval: ExecutionInterval;
}

export const TrackingEndedFacts: React.FC<Props> = ({ position, executionInterval }) => {
  const outsideLabel = (time: string) =>
    isFactOutsideExecutionInterval(time, executionInterval) ? (
      <small className="TrackingEndedOutsideInterval">{OUTSIDE_EXECUTION_INTERVAL_LABEL}</small>
    ) : null;

  return (
    <div className="TrackingEndedFacts">
      <TrackingEndedBadge />
      {executionInterval == null && (
        <small className="TrackingEndedIntervalUnavailable">{EXECUTION_INTERVAL_UNAVAILABLE}</small>
      )}
      <div className="TrackingEndedEntryFact">
        <span className="TrackingEndedFactTime">
          {position.start_ts ? formatDateTimeUTC(position.start_ts) : "-"}
        </span>
        <span className="TrackingEndedEntryPrice">
          Price: {position.entry_price?.toFixed(4) ?? "-"}
        </span>
        <span className="TrackingEndedEntryAmount">
          Contracts: {position.entry_amount?.toFixed(4) ?? "-"}
        </span>
        <span className="TrackingEndedCollateral">
          Collateral: {position.collateral_in?.toFixed(4) ?? "-"}
        </span>
        <span className="TrackingEndedFees">
          Total recorded position fees: {position.total_fees_usdt?.toFixed(4) ?? "-"}
        </span>
        {outsideLabel(position.start_ts)}
      </div>
      {(position.movements ?? []).map((movement) => {
        const time = getTrackingEndedMovementTime(movement);
        const action = ENTRY_ACTION_IDS.includes(movement.action_id)
          ? "BUY"
          : EXIT_ACTION_IDS.includes(movement.action_id)
            ? "SELL"
            : `ACTION_${movement.action_id}`;
        return (
          <div className="TrackingEndedMovementFact" key={movement.id}>
            <span className="TrackingEndedMovementAction">{action}</span>
            <span className="TrackingEndedFactTime">{time ? formatDateTimeUTC(time) : "-"}</span>
            <span className="TrackingEndedMovementPrice">
              Price: {movement.price?.toFixed(4) ?? "-"}
            </span>
            <span className="TrackingEndedMovementAmount">
              Contracts: {movement.amount?.toFixed(4) ?? "-"}
            </span>
            <span className="TrackingEndedFees">Fees: {movement.fees_usdt?.toFixed(4) ?? "-"}</span>
            {outsideLabel(time)}
          </div>
        );
      })}
      {!position.movements?.length && (
        <small className="TrackingEndedNoMovements">{NO_RECORDED_MOVEMENTS}</small>
      )}
    </div>
  );
};
